/**
 * The preferences CONTENT — auto-translate, the language anchors, currency and
 * units. ONE copy, worn by every chrome that offers them.
 *
 * WHY IT IS ITS OWN COMPONENT (F5). Until this split the content lived inside
 * `PreferencesModal`, welded to the navbar's globe trigger — and
 * `NavbarShell`'s `<header>` is `sticky top-0 hidden … md:block`. So on a
 * phone the ONLY language control in the app was inside chrome the breakpoint
 * removed: measured on the hermetic fleet at 393×852, on `/`, on an account
 * listing and on an editor step, `visibleLanguageButtons 0` every time. A
 * visitor who cannot read the page had no way to change the language.
 *
 * The remedy is a second OPENER, never a second copy — a copy is how the two
 * drift, and this content is a vocabulary (locales, currencies, unit systems)
 * that is DERIVED from generated SSOTs. So the body is the unit: the navbar's
 * globe ({@link PreferencesModal}) and the profile hub's row
 * ({@link PreferencesRow}) both render THIS, and adding a third surface adds
 * no strings, no options and no divergence.
 *
 * LANGUAGE IS A LIST OF ANCHORS, not a toggle group. A locale switch on the
 * public app is a full-document navigation by design (the SSR `IntlProvider`
 * is fixed above the router), and a navigation the browser owns is an
 * `<a href hrefLang>`: middle-click opens the other language beside this one,
 * copy-link copies it, and it works before hydration. Pages with localized
 * slugs (blog posts, listings) carry pre-fetched per-locale URLs from
 * `AlternatesContext`; every other page re-prefixes the current path through
 * the routing seam's `localeSwitchHref` — the same computation `switchLocale`
 * commits. Until LET-83's residue was closed this was a `ToggleGroup` whose
 * `onValueChange` called `window.location.assign`: a target that existed only
 * in JS.
 *
 * ★ That anchor property is what makes the no-JavaScript leg of the profile
 * row COMPLETE rather than merely reachable: the row is a real link to
 * `?preferences=open`, the page server-renders this body under it, and every
 * language option inside is a link the browser can follow on its own. A reader
 * with no JavaScript finishes the whole journey.
 */
import { useAlternates } from '@/shared/hooks/useAlternates';
import { usePreferences } from '@/shared/hooks/usePreferences';
import { localeSwitchHref, usePathname, useSearchParams } from '@/shared/i18n/routing';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import {
  Currency,
  CURRENCY_OPTIONS,
  currencyToWire,
  LOCALE_OPTIONS,
  UNIT_SYSTEM_OPTIONS,
  UnitSystem,
  unitSystemToWire,
} from '@/shared/types/preferences';
import { wireToCanonical } from '@letsgethome/lib/proto-enums/common/wire_aliases_gen';
import { plaintextString, type Locale } from '@letsgethome/types/branded';
import { GroupLabel, Label, useLabelledGroup } from '@letsgethome/ui/label';
import { cn } from '@letsgethome/ui/lib/utils';
import { Switch } from '@letsgethome/ui/switch';
import { toggleVariants } from '@letsgethome/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@letsgethome/ui/toggle-group';

/**
 * The label the CURRENT locale is shown by — the locale's own endonym, from
 * the generated locale SSOT (`LOCALE_OPTIONS`). Exported because the profile
 * row displays it as the row's value, and a row that spelled its own endonym
 * would be a second list to keep in step with `common.v1.Locale`.
 */
export function localeLabel(locale: Locale): string {
  return LOCALE_OPTIONS.find((option) => option.value === locale)?.label ?? locale;
}

/** The preferences content, chrome-free. See the module doc. */
export function PreferencesBody() {
  const t = useTranslations('preferences');
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const alternates = useAlternates();

  const { preferences, setAutoTranslate, setCurrency, setUnitSystem } = usePreferences();

  // The current query rides along on a same-page locale switch (a filtered
  // directory stays filtered); the hash is not part of the server render.
  const search = searchParams.toString();
  const localeHref = (target: Locale) =>
    alternates?.[target] ?? localeSwitchHref(target, pathname, search ? `?${search}` : '');

  // LET-134. Three of this sheet's four sections put a `<Label>` over a SET —
  // and a `<Label>` names the one control it points at, so over a set it names
  // nothing. The two `ToggleGroup`s are the sharp case: Radix gives them
  // `role="group"`, every `ToggleGroupItem` inside carries its own `aria-label`,
  // and the result announced "HKD, button" with no sentence anywhere saying
  // "Currency". Both are named by their own visible heading now.
  const currency = useLabelledGroup('group');
  const unitSystem = useLabelledGroup('group');
  // The language section is NOT this class and is not treated as one: a `<nav>`
  // is a LANDMARK, already named by its `aria-label`, and stamping `role="group"`
  // on it would destroy the landmark to fix nothing. What it owed was the other
  // half — its heading was a `<label>` element labelling nothing, which is what
  // `GroupLabel` exists to stop being.
  return (
    <div className="space-y-6 py-4">
      {/* Auto-translate */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="auto-translate" className="text-base font-medium">
            {t('autoTranslate.label')}
          </Label>
          <p className="text-sm text-muted-foreground">{t('autoTranslate.description')}</p>
        </div>
        <Switch
          id="auto-translate"
          checked={preferences.autoTranslate}
          onCheckedChange={setAutoTranslate}
        />
      </div>

      {/* Language — real anchors; see the module doc. The current locale
          is marked `aria-current` and styled as the pressed toggle. */}
      <div className="space-y-3">
        <GroupLabel className="text-base font-medium">{t('language.label')}</GroupLabel>
        <nav aria-label={t('language.label')} className="flex flex-wrap justify-start gap-2">
          {LOCALE_OPTIONS.map((option) => {
            const current = option.value === locale;
            return (
              <a
                key={option.value}
                href={localeHref(option.value)}
                hrefLang={option.value}
                lang={option.value}
                aria-current={current ? 'true' : undefined}
                className={cn(
                  toggleVariants({ variant: 'outline' }),
                  'px-4 no-underline',
                  current && 'bg-accent text-accent-foreground'
                )}
              >
                {plaintextString(option.label)}
              </a>
            );
          })}
        </nav>
      </div>

      {/* Currency */}
      <div className="space-y-3">
        <GroupLabel {...currency.labelProps} className="text-base font-medium">
          {t('currency.label')}
        </GroupLabel>
        <ToggleGroup
          {...currency.groupProps}
          type="single"
          value={currencyToWire(preferences.currency)}
          onValueChange={(value) => {
            if (!value) return;
            try {
              const canonical = wireToCanonical('common.v1.Currency', value);
              const next = Currency[canonical as keyof typeof Currency] as Currency | undefined;
              if (next !== undefined) setCurrency(next);
            } catch {
              // Invalid toggle value: ignore (UI never produces these).
            }
          }}
          variant="outline"
          spacing={2}
          className="flex-wrap justify-start"
        >
          {CURRENCY_OPTIONS.map((option) => (
            <ToggleGroupItem
              key={option.labelKey}
              value={option.labelKey}
              aria-label={t(`currency.options.${option.labelKey}`)}
              className="px-4"
            >
              {plaintextString(option.symbol)} {plaintextString(option.labelKey.toUpperCase())}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {/* Unit System */}
      <div className="space-y-3">
        <GroupLabel {...unitSystem.labelProps} className="text-base font-medium">
          {t('unitSystem.label')}
        </GroupLabel>
        <ToggleGroup
          {...unitSystem.groupProps}
          type="single"
          value={unitSystemToWire(preferences.unitSystem)}
          onValueChange={(value) => {
            if (!value) return;
            try {
              const canonical = wireToCanonical('common.v1.UnitSystem', value);
              const next = UnitSystem[canonical as keyof typeof UnitSystem] as
                | UnitSystem
                | undefined;
              if (next !== undefined) setUnitSystem(next);
            } catch {
              // Invalid toggle value: ignore (UI never produces these).
            }
          }}
          variant="outline"
          spacing={2}
          className="flex-wrap justify-start"
        >
          {UNIT_SYSTEM_OPTIONS.map((option) => (
            <ToggleGroupItem
              key={option.labelKey}
              value={option.labelKey}
              aria-label={t(`unitSystem.options.${option.labelKey}`)}
              className="px-4"
            >
              {t(`unitSystem.options.${option.labelKey}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
    </div>
  );
}
